/**
 * Ключи типов связей, по которым заданы маркеры (должны совпадать с меню и Concept.js).
 */
const RELATIONSHIP_TYPE_KEYS = [
	'Composition',
	'Aggregation',
	'Assignment',
	'Realization',
	'Serving',
	'Access',
	'Influence',
	'Association',
	'Triggering',
	'Flow',
	'Specialization',
];

/**
 * Приводит тип связи из меню/модели к ключу конфига (без учёта регистра и пробелов).
 * @param {string} type
 * @returns {string} ключ для configMap или 'Association'
 */
export function canonicalizeRelationshipType(type) {
	if (!type || typeof type !== 'string') return 'Association';
	const t = type.trim();
	if (!t) return 'Association';
	const lower = t.toLowerCase();
	const found = RELATIONSHIP_TYPE_KEYS.find(k => k.toLowerCase() === lower);
	if (found) return found;
	// "And Junction" / "Or Junction" и т.п. — по умолчанию Association
	if (lower.includes('junction') || lower.includes('and') || lower.includes('or'))
		return 'Association';
	return 'Association';
}

/**
 * Конфигурация рёбер X6: маркеры по типу связи ArchiMate (relationshipType, accessType).
 * Используется EdgeFactory и renderView.
 * Тип приводится к ключу через canonicalizeRelationshipType.
 */
export function getEdgeMarkerConfigForRelationshipType(relationshipType, options = {}) {
	const {accessType} = options;
	const canonicalType = canonicalizeRelationshipType(relationshipType);

	const openTriangle = {name: 'block', open: true, width: 8, height: 8};
	const filledTriangle = {name: 'block', open: false, width: 8, height: 8};
	const filledDiamond = {name: 'diamond', open: false, width: 15, height: 10};
	const hollowDiamond = {name: 'diamond', open: false, width: 15, height: 10, fill: 'white'};
	const dot = {name: 'circle', open: false, r: 4};
	const closedBlankTriangle = {
		name: 'path',
		d: 'M 11 5 L 1 10 L 11 15 Z',
		fill: 'white',
		stroke: '#000000',
		offsetX: -5,
		offsetY: 0,
	};

	if (canonicalType === 'Access') {
		const isWrite = accessType === 'Write' || accessType === 'ReadWrite';
		const isRead = accessType === 'Read' || accessType === 'ReadWrite';
		return {
			sourceMarker: isRead ? openTriangle : undefined,
			targetMarker: isWrite ? openTriangle : undefined,
			strokeDasharray: '2 2',
		};
	}

	const configMap = {
		Composition: {sourceMarker: filledDiamond, targetMarker: null, strokeDasharray: 'none'},
		Aggregation: {sourceMarker: hollowDiamond, targetMarker: null, strokeDasharray: 'none'},
		Assignment: {sourceMarker: dot, targetMarker: filledTriangle, strokeDasharray: 'none'},
		Association: {sourceMarker: null, targetMarker: null, strokeDasharray: 'none'},
		Flow: {sourceMarker: null, targetMarker: filledTriangle, strokeDasharray: '6 3'},
		Influence: {sourceMarker: null, targetMarker: openTriangle, strokeDasharray: '6 3'},
		Realization: {
			sourceMarker: null,
			targetMarker: closedBlankTriangle,
			strokeDasharray: '2 2',
		},
		Serving: {sourceMarker: null, targetMarker: openTriangle, strokeDasharray: 'none'},
		Specialization: {
			sourceMarker: null,
			targetMarker: closedBlankTriangle,
			strokeDasharray: 'none',
		},
		Triggering: {sourceMarker: null, targetMarker: filledTriangle, strokeDasharray: 'none'},
	};

	const config = configMap[canonicalType];
	if (!config) return {targetMarker: {name: 'classic', size: 8}};
	return {
		sourceMarker: config.sourceMarker,
		targetMarker: config.targetMarker,
		strokeDasharray: config.strokeDasharray,
	};
}

/**
 * Собрать attrs.line для edge по типу связи, цвету и толщине.
 * Все поля (sourceMarker, targetMarker, strokeDasharray) задаются явно, чтобы при смене типа
 * старые значения не сохранялись из-за merge в X6.
 * В X6 маркер убирается только при явном attr('line/…Marker', '') или removeAttrByPath (null/false не снимают).
 * Для «нет маркера» передаём пустую строку ''.
 */
export function buildEdgeLineAttrs(relationshipType, options = {}) {
	const {accessType, lineColor, lineWidth} = options;
	const markerConfig = getEdgeMarkerConfigForRelationshipType(relationshipType, {accessType});

	return {
		stroke: lineColor ?? '#000000',
		strokeWidth: lineWidth != null && lineWidth >= 0 ? Number(lineWidth) : 1,
		sourceMarker: markerConfig.sourceMarker ?? '',
		targetMarker: markerConfig.targetMarker ?? '',
		strokeDasharray: markerConfig.strokeDasharray ?? 0,
	};
}
